import { CardItem } from '@/components/Card/Card';
import { Link } from '@/components/Link/Link';
import Stack from '@/design_system/Stack';
import Paragraph from '@/design_system/Typography/Paragraph';
import { Organization } from '@/models/organization';
import { getRequestorType } from '@/models/request';
import { ArticleWithRelations } from '@/models/shipment';
import { getScopedBoolean, useWorkflowPublic } from '@/models/workflow';
import { requestorTypeIcons } from '@/routes/Requests/Tabs/common';
import { createBEMClasses } from '@/utils/classname';
import { useRequestName } from '@/utils/useRequestName';

import './ArticleRequestCell.css';

const { block, element } = createBEMClasses('article-request-cell');

const ArticleRequestCell = ({
  article,
  organization,
}: {
  article: ArticleWithRelations;
  organization: Organization;
}) => {
  const request = (article.request ?? article.archivedRequest)!;
  const { data: workflow } = useWorkflowPublic(request.workflowId);
  const isClientEnabled = getScopedBoolean({
    scopedBoolean: workflow?.config.fields?.request?.client?.enabled,
    credentialsScopes: article.scopes,
  });

  const requestName = useRequestName({
    request: { ...request, organization },
    isClientEnabled,
  });

  return (
    <Link to={`/requests/${request.id}`} className={block(undefined, 'reset-link-style')}>
      <Stack row gap="0.5rem" flexWrap="nowrap">
        <div className={element('requestor-type')}>
          {requestorTypeIcons[getRequestorType(request)]}
        </div>
        <div>
          <Paragraph weight="medium">[{request.reference}]</Paragraph>
          <Paragraph size="200">
            <>
              {requestName.major} {!!requestName.minor && `(${requestName.minor})`}
            </>
          </Paragraph>
        </div>
      </Stack>
    </Link>
  );
};

export default ArticleRequestCell;

export const ArticleRequestCardItem = ({
  article,
  organization,
}: {
  article: ArticleWithRelations;
  organization: Organization;
}) => {
  const request = (article.request ?? article.archivedRequest)!;
  const { data: workflow } = useWorkflowPublic(request.workflowId);
  const isClientEnabled = getScopedBoolean({
    scopedBoolean: workflow?.config.fields?.request?.client?.enabled,
    credentialsScopes: article.scopes,
  });

  const requestName = useRequestName({
    request: { ...request, organization },
    isClientEnabled,
  });

  return (
    <Link to={`/requests/${request.id}`} className={block(undefined, 'reset-link-style')}>
      <CardItem>
        <div style={{ fontSize: '1rem' }}>{requestorTypeIcons[getRequestorType(request)]}</div>
        <span>
          [{request.reference}] {requestName.major}{' '}
          {!!requestName.minor && `(${requestName.minor})`}
        </span>
      </CardItem>
    </Link>
  );
};
